// How often to try and detect a new device type
const WHEEL_INACTIVITY_THRESHOLD = 200;

/**
 * DeviceTypeDetector - Distinguishes between mouse and trackpad input devices
 *
 * Since browsers don't provide device type information natively, we use heuristics
 * to detect the input device type based on wheel event characteristics:
 * - Trackpads often generate deltaX values, while mice typically don't
 * - Trackpads produce fractional deltaY values when pinching
 * - Mice generally have larger wheelDelta values than trackpads
 * - Trackpad pinch events include the ctrl key
 *
 * This detection enables different scrolling behaviors:
 * - Mouse scroll: zooms the canvas
 * - Trackpad scroll: pans the canvas
 * - Trackpad pinch: zooms the canvas
 *
 * Note: Detection may occasionally misidentify trackpads as mice when scrolling
 * strictly vertical with no horizontal component.
 */

export class DeviceTypeDetector {
  private lastWheelEventTime = 0;
  private cachedDeviceType: 'mouse' | 'trackpad' = 'mouse';

  public detectDeviceType(event: WheelEvent): 'mouse' | 'trackpad' {
    const { deltaX, deltaY } = event;
    const wheelDeltaX = (event as any).wheelDeltaX;
    const wheelDeltaY = (event as any).wheelDeltaY;
    const currentTime = Date.now();
    const timeSinceLastEvent = currentTime - this.lastWheelEventTime;

    this.lastWheelEventTime = currentTime;

    // Mice don't send deltaX, but trackpads do. We should always switch if we see this
    if (deltaX !== 0 || wheelDeltaX !== 0) {
      this.cachedDeviceType = 'trackpad';
    }

    // If this isn't the first event in a sequence, return cached type. Only the
    // initial event data gives us values that indicate device type. There are
    // rare cases where the first event has large delta values, but most of the time
    // we get indicative data.
    if (timeSinceLastEvent < WHEEL_INACTIVITY_THRESHOLD) {
      return this.cachedDeviceType;
    }

    // Trackpads send fractional deltaY when pinching, mice do not seem to
    if (deltaY !== 0 && Math.abs(deltaY) < 1) {
      this.cachedDeviceType = 'trackpad';
    }

    // Safari has lowest wheelDeltaY of 12 for mice
    if (wheelDeltaY && Math.abs(wheelDeltaY) >= 12) {
      // When pinching on trackpads, the event has ctrlKey set. Doesn't really
      // matter if the user is actually using a mouse in this case, as we will
      // zoom anyway. But, we would not want the next event to behave like a mouse
      // if the user is using a trackpad.
      if (event.ctrlKey) {
        this.cachedDeviceType = 'trackpad';
      } else {
        this.cachedDeviceType = 'mouse';
      }
    }

    // We default to mouse on load
    return this.cachedDeviceType;
  }

  public getCurrentDetectedType(): 'mouse' | 'trackpad' {
    return this.cachedDeviceType;
  }
}

export default DeviceTypeDetector;
